import { useMemo } from 'react'

import { Form } from 'factorial-form'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction from 'modules/core/lib/asyncAction'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import { track } from 'modules/core/lib/metrics'
import companies from 'modules/core/store/collections/companies'
import legalEntities, { companyLegalEntities } from 'modules/core/store/collections/legalEntities'

import AddressForm from 'modules/billing/lib/forms/PersonalForm'
import IdentificationForm from 'modules/settings/lib/forms/Identification'

interface Props {
  createSubscription: () => Promise<void>
  onFail: () => void
}

interface ReturnType {
  addressForm: AddressForm
  identificationForm: IdentificationForm
  legalEntityForm: Form
  onSubmit: () => Promise<unknown>
  submitting: boolean
}

const useBillingInfo = ({ createSubscription }: Props): ReturnType => {
  const graphQLClient = useGraphQLClient()

  // Legal Entity
  const legalEntityForm = useMemo(
    () =>
      new Form(
        {
          legal_entity_id: companyLegalEntities(companies.current)[0]?.attributes.id,
        },
        {
          legal_entity_id: 'string',
        }
      ),
    []
  )

  const legalEntityId = legalEntityForm.get('legal_entity_id').value

  const legalEntity = useMemo(
    () =>
      legalEntities.find({
        id: legalEntityId,
      }),
    [legalEntityId]
  )

  const entityAttrs = useMemo(() => legalEntity?.toJS(), [legalEntity])

  // Forms
  const addressForm = useMemo(() => new AddressForm(entityAttrs ?? {}), [entityAttrs])

  const identificationForm = useMemo(() => new IdentificationForm(entityAttrs ?? {}), [entityAttrs])

  // onSubmit
  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async () => {
      const data = { ...identificationForm.data(), ...addressForm.data() }

      if (!legalEntity) {
        return
      }

      const { success } = await asyncAction(() => legalEntity.save(data), {
        successFlash: false,
      })
      track(graphQLClient, 'LEGAL_ENTITY_VALIDATION', {
        is_valid: !!success,
      })

      if (success) {
        await createSubscription()
      }
    },
    {
      successFlash: false,
    }
  )

  return {
    addressForm,
    identificationForm,
    legalEntityForm,
    onSubmit,
    submitting,
  }
}

export default useBillingInfo
